import { useSendAudio } from '@hakimo-ui/hakimo/data-access';
import { UnifiedMonitoringProduct } from '@hakimo-ui/hakimo/types';
import { toast } from '@hakimo-ui/hakimo/util';
import { Button, HakimoSpinner } from '@hakimo-ui/shared/ui-base';
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { MicrophoneIcon } from '@heroicons/react/24/solid';
import { MutableRefObject, useCallback, useState } from 'react';
import AudioUploadModal from './AudioUploadModal';
import Recorder from './Recorder';

interface Props {
  cameraId: string;
  cameraName?: string;
  alarmId?: string;
  locAlarmId?: string;
  escalationId?: string;
  onTalkdown?: (cameraId: string, alarmId?: string) => void;
  onToggle?: (open: boolean) => void;
  product?: UnifiedMonitoringProduct;
  portalTarget?: MutableRefObject<HTMLElement | null>;
}

export function AudioRecorder(props: Props) {
  const {
    cameraId,
    alarmId,
    onTalkdown,
    onToggle,
    locAlarmId,
    escalationId,
    cameraName = '',
    product,
    portalTarget,
  } = props;
  const [record, setRecord] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const mutation = useSendAudio(
    cameraId,
    alarmId,
    locAlarmId,
    () => {
      onTalkdown?.(cameraId, alarmId || locAlarmId || escalationId);
      toast('Audio sent to camera successfully');
    },
    escalationId,
    product
  );

  const onSubmitAudio = (file: File) => {
    const formData = new FormData();
    formData.append('audio', file);
    mutation.mutate(formData);
  };

  const onCloseRecorder = useCallback(() => {
    setRecord(false);
    onToggle && onToggle(false);
  }, [onToggle]);

  const onOpenRecorder = () => {
    setRecord(true);
    onToggle && onToggle(true);
  };

  const showUploadModal = () => setUploadModalVisible(true);

  const onCloseAudioModal = () => setUploadModalVisible(false);

  return (
    <>
      <div className="relative flex items-center justify-end">
        {mutation.isLoading && (
          <span className="absolute inset-0 z-10 flex items-center justify-center">
            <HakimoSpinner />
          </span>
        )}
        {record ? (
          <Recorder onClose={onCloseRecorder} onSubmitAudio={onSubmitAudio} />
        ) : (
          <span className="flex items-center gap-2">
            <Button
              variant="icon"
              onClick={showUploadModal}
              title="Audio talkdown audio upload"
            >
              <ArrowUpTrayIcon className="h-5 w-5" />
            </Button>
            <Button
              variant="icon"
              onClick={onOpenRecorder}
              title="Live camera audio talkdown"
            >
              <MicrophoneIcon className="h-5 w-5" />
            </Button>
          </span>
        )}
      </div>
      {uploadModalVisible && (
        <AudioUploadModal
          onClose={onCloseAudioModal}
          open={uploadModalVisible}
          cameraName={cameraName}
          onSubmitAudio={onSubmitAudio}
          portalTarget={portalTarget}
        />
      )}
    </>
  );
}

export default AudioRecorder;
